import { useCallback, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { TestExecutionDetailsPanels } from '@/features/executions/components/TestExecutionDetailsPanels';
import {
  buildTestDetails,
  useCopyPanel,
} from '@/features/uploads/components/resultsFileDetailUtils';
import { ResultsFileOutputPanel } from '@/features/uploads/components/ResultsFileOutputPanel';
import { s } from '@/theme/tokens';
import { DetailPageTemplate, DetailTitlePrefix } from '@/ui/composites/scaffolds';
import { formatDuration } from '@/ui/formatters/formatDuration';
import { Section } from '@/ui/layout/Section';
import { Stack } from '@/ui/layout/Stack';
import {
  Box,
  Button,
  Chip,
  Link,
  resolveStatusChipTone,
  Surface,
  Text,
  formatStatusLabel,
} from '@/ui/primitives';
import type { BuildDto, TestExecutionDto } from '@flowqa/shared';
import type { ReactElement } from 'react';

type TestExecutionDetailTitle = {
  name: string;
  runNumber: number;
};

type TestExecutionDetailContentProps = {
  title: string | TestExecutionDetailTitle;
  execution: TestExecutionDto;
  build: BuildDto | null;
  suiteExecutionRunNumber?: number | null;
  onDelete?: () => Promise<void> | void;
  deleteLoading?: boolean;
};

export const TestExecutionDetailContent = ({
  title,
  execution,
  build,
  suiteExecutionRunNumber = null,
  onDelete,
  deleteLoading = false,
}: TestExecutionDetailContentProps): ReactElement => {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const copyPanel = useCopyPanel();
  const handleDeleteConfirm = useCallback(() => {
    setDeleteOpen(false);
    if (onDelete) {
      void onDelete();
    }
  }, [onDelete]);
  const testDetails = buildTestDetails(execution);
  const titleLabel = typeof title === 'string' ? title : title.name;
  const runNumberLabel = typeof title === 'string' ? null : `#${title.runNumber}`;
  const durationLabel =
    execution.durationMs !== null && execution.durationMs !== undefined
      ? formatDuration(execution.durationMs)
      : null;
  const testHref = execution.testSlug
    ? `/tests/${execution.testSlug}`
    : execution.testId
      ? `/tests/${execution.testId}`
      : null;

  return (
    <>
      <ConfirmDialog
        open={deleteOpen}
        title="Delete Test Execution"
        description={
          <Stack gap="sm">
            <Text role="body" color="textSecondary">
              Deleting will remove this test execution record.
            </Text>
            <Text role="body" color="textSecondary">
              This action cannot be undone.
            </Text>
          </Stack>
        }
        confirmLabel="Delete Test Execution"
        confirmColor="error"
        confirmVariant="contained"
        loading={deleteLoading}
        onClose={() => {
          setDeleteOpen(false);
        }}
        onConfirm={handleDeleteConfirm}
      />
      <DetailPageTemplate
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: s('md'), minWidth: 0 }}>
            <DetailTitlePrefix label="Test Execution" nowrap />
            {testHref ? (
              <Link component={RouterLink} to={testHref} underline="hover" color="inherit">
                <Text
                  role="sectionTitle"
                  component="span"
                  sx={{ minWidth: 0, maxWidth: '100%', whiteSpace: 'normal' }}
                >
                  {titleLabel}
                </Text>
              </Link>
            ) : (
              <Text
                role="sectionTitle"
                component="h1"
                sx={{ minWidth: 0, maxWidth: '100%', whiteSpace: 'normal' }}
              >
                {titleLabel}
              </Text>
            )}
            {runNumberLabel ? (
              <Text role="sectionTitle" component="span" color="textSecondary">
                {runNumberLabel}
              </Text>
            ) : null}
          </Box>
        }
        status={
          <>
            {execution.isLatest ? (
              <Chip label="Latest" tone="primary" transform="uppercase" size="small" />
            ) : null}
            <Chip
              label={formatStatusLabel(execution.status)}
              tone={resolveStatusChipTone(execution.status)}
              transform="uppercase"
              size="small"
            />
          </>
        }
        headerMeta={
          durationLabel ? (
            <Text role="bodyMuted" component="span" sx={{ whiteSpace: 'nowrap' }}>
              {durationLabel}
            </Text>
          ) : null
        }
        headerActions={
          onDelete ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="primary"
                size="md"
                color="error"
                onClick={() => {
                  setDeleteOpen(true);
                }}
                disabled={deleteLoading}
                sx={{ color: 'common.white', '&:hover': { color: 'common.white' } }}
              >
                Delete
              </Button>
            </Box>
          ) : null
        }
        metadata={
          <TestExecutionDetailsPanels
            execution={execution}
            build={build}
            suiteExecutionRunNumber={suiteExecutionRunNumber}
          />
        }
        primaryContent={
          testDetails.length > 0 ? (
            <Section paddingY="md">
              <Surface padding="lg" border>
                <Stack gap="md">
                  <Text role="sectionTitle">Output</Text>
                  <ResultsFileOutputPanel items={testDetails} onCopy={copyPanel} itemGap="lg" />
                </Stack>
              </Surface>
            </Section>
          ) : null
        }
      />
    </>
  );
};
